(() => {
  "use strict";

  const API_ENDPOINT = "https://chappal-operasyon-merkezi.vercel.app/api/kosk/visit/request";
  const TIMEZONE = "Europe/Istanbul";
  const REQUEST_TIMEOUT_MS = 15000;
  const SLOT_NAMES = ["preferredAt", "alternative1At"];
  const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  const messages = {
    tr: {
      summary: "Lütfen aşağıdaki alanları kontrol edin:",
      required: "Bu alan zorunludur.",
      fullName: "Lütfen adınızı ve soyadınızı yazın.",
      email: "Geçerli bir e-posta adresi yazın.",
      phone: "Geçerli bir telefon numarası yazın.",
      purpose: "Ziyaret amacını en az 10 karakterle açıklayın.",
      guests: "Kişi sayısı 1 ile 20 arasında olmalıdır.",
      timePast: "Gelecekte bir zaman seçin.",
      timeHoursWeekday: "Hafta içi başlangıç saati 10.00 ile 17.00 arasında olmalıdır.",
      timeHoursWeekend: "Hafta sonu başlangıç saati 11.00 ile 17.00 arasında olmalıdır.",
      timeStep: "Başlangıç saati tam veya buçuk olmalıdır.",
      timeDuplicate: "İki tercih aynı olamaz.",
      consent: "Devam etmek için KVKK onayını işaretleyin.",
      submit: "Talep şu anda gönderilemedi. Bilgileriniz kaybolmadı; lütfen tekrar deneyin.",
      timeout: "Sunucu yanıt vermedi. Bilgileriniz kaybolmadı; lütfen tekrar deneyin."
    },
    en: {
      summary: "Please review the following fields:",
      required: "This field is required.",
      fullName: "Enter your first and last name.",
      email: "Enter a valid email address.",
      phone: "Enter a valid phone number.",
      purpose: "Describe the purpose of your visit in at least 10 characters.",
      guests: "The number of guests must be between 1 and 20.",
      timePast: "Choose a time in the future.",
      timeHoursWeekday: "On weekdays, the start time must be between 10:00 and 17:00.",
      timeHoursWeekend: "On weekends, the start time must be between 11:00 and 17:00.",
      timeStep: "The start time must be on the hour or half hour.",
      timeDuplicate: "The two choices must be different.",
      consent: "Accept the privacy consent to continue.",
      submit: "The request could not be sent. Your entries are still here; please try again.",
      timeout: "The server did not respond. Your entries are still here; please try again."
    }
  };

  const createRequestId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
  };

  const istanbulNow = () => {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23"
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
  };

  const parseLocalSlot = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
    if (!match) return null;

    const [, year, month, day, hour, minute] = match.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return {
      value,
      weekday: date.getUTCDay(),
      minutes: hour * 60 + minute
    };
  };

  const init = (root) => {
    if (root.dataset.koskVisitBound === "true") return;
    root.dataset.koskVisitBound = "true";

    const locale = root.dataset.locale === "tr" ? "tr" : "en";
    const copy = messages[locale];
    const form = root.querySelector("[data-kosk-visit-form]");
    if (!form) return;

    const requestIdInput = form.querySelector("[data-request-id-input]");
    const submitButton = form.querySelector("[data-submit]");
    const submitLabel = form.querySelector("[data-submit-label]");
    const submitBusy = form.querySelector("[data-submit-busy]");
    const submitIcon = form.querySelector("[data-submit-icon]");
    const summary = form.querySelector("[data-error-summary]");
    const summaryTitle = form.querySelector("[data-error-summary-title]");
    const summaryList = form.querySelector("[data-error-summary-list]");
    const submitError = form.querySelector("[data-submit-error]");
    const successState = root.querySelector("[data-success-state]");
    const successId = root.querySelector("[data-success-request-id]");
    const requestId = createRequestId();

    requestIdInput.value = requestId;
    SLOT_NAMES.forEach((name) => {
      const input = form.elements.namedItem(name);
      if (input) input.min = istanbulNow();
    });
    submitButton.disabled = false;

    const setBusy = (isBusy) => {
      submitButton.disabled = isBusy;
      submitButton.setAttribute("aria-busy", String(isBusy));
      submitLabel.hidden = isBusy;
      submitBusy.hidden = !isBusy;
      submitIcon.hidden = isBusy;
    };

    const clearFieldError = (name) => {
      const input = form.elements.namedItem(name);
      const error = form.querySelector(`[data-error-for="${name}"]`);
      if (input) input.removeAttribute("aria-invalid");
      if (error) {
        error.textContent = "";
        error.hidden = true;
      }
    };

    const showFieldError = (name, message) => {
      const input = form.elements.namedItem(name);
      const error = form.querySelector(`[data-error-for="${name}"]`);
      if (input) input.setAttribute("aria-invalid", "true");
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    };

    const validate = () => {
      const values = Object.fromEntries(new FormData(form).entries());
      const errors = new Map();
      const fullName = String(values.fullName || "").trim();
      const email = String(values.email || "").trim();
      const phone = String(values.phone || "").trim();
      const purpose = String(values.purpose || "").trim();
      const guestCount = Number(values.guestCount);

      if (fullName.length < 3) errors.set("fullName", copy.fullName);

      const emailInput = form.elements.namedItem("email");
      if (!email || (emailInput && emailInput.validity.typeMismatch)) {
        errors.set("email", copy.email);
      }

      const phoneDigits = phone.replace(/\D/g, "");
      if (phoneDigits.length < 7 || phoneDigits.length > 15) {
        errors.set("phone", copy.phone);
      }

      if (purpose.length < 10) errors.set("purpose", copy.purpose);
      if (!Number.isInteger(guestCount) || guestCount < 1 || guestCount > 20) {
        errors.set("guestCount", copy.guests);
      }

      const now = istanbulNow();
      const seenSlots = new Map();
      SLOT_NAMES.forEach((name) => {
        const value = String(values[name] || "");
        const isOptional = name === "alternative1At";
        const slot = parseLocalSlot(value);

        if (!value && isOptional) return;
        if (!value || !slot) {
          errors.set(name, copy.required);
          return;
        }
        if (slot.value <= now) {
          errors.set(name, copy.timePast);
          return;
        }
        const isWeekend = slot.weekday === 0 || slot.weekday === 6;
        const earliestStart = isWeekend ? 11 * 60 : 10 * 60;
        if (slot.minutes < earliestStart || slot.minutes > 17 * 60) {
          errors.set(name, isWeekend ? copy.timeHoursWeekend : copy.timeHoursWeekday);
          return;
        }
        if (slot.minutes % 30 !== 0) {
          errors.set(name, copy.timeStep);
          return;
        }

        if (seenSlots.has(slot.value)) {
          errors.set(name, copy.timeDuplicate);
          errors.set(seenSlots.get(slot.value), copy.timeDuplicate);
        } else {
          seenSlots.set(slot.value, name);
        }
      });

      if (!form.elements.namedItem("kvkk").checked) {
        errors.set("kvkk", copy.consent);
      }

      return { errors, values };
    };

    const renderErrors = (errors) => {
      form.querySelectorAll("[data-error-for]").forEach((element) => {
        const name = element.getAttribute("data-error-for");
        clearFieldError(name);
      });
      summaryList.replaceChildren();

      if (!errors.size) {
        summary.hidden = true;
        return;
      }

      errors.forEach((message, name) => {
        showFieldError(name, message);
        const input = form.elements.namedItem(name);
        if (!input || !input.id) return;
        const item = document.createElement("li");
        const link = document.createElement("a");
        link.href = `#${input.id}`;
        link.textContent = message;
        item.appendChild(link);
        summaryList.appendChild(item);
      });

      summaryTitle.textContent = copy.summary;
      summary.hidden = false;
      summary.focus();
    };

    form.addEventListener("input", (event) => {
      if (event.target && event.target.name) clearFieldError(event.target.name);
      summary.hidden = true;
      submitError.hidden = true;
    });

    form.addEventListener("change", (event) => {
      if (event.target && event.target.name) clearFieldError(event.target.name);
      summary.hidden = true;
      submitError.hidden = true;
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      submitError.hidden = true;

      const { errors, values } = validate();
      renderErrors(errors);
      if (errors.size) return;

      const alternativeSlot = String(values.alternative1At || "").trim();
      const payload = {
        fullName: String(values.fullName).trim(),
        email: String(values.email).trim(),
        phone: String(values.phone).trim(),
        company: String(values.company || "").trim(),
        purpose: String(values.purpose).trim(),
        partySize: Number(values.guestCount),
        preferredSlot: String(values.preferredAt),
        alternativeSlots: alternativeSlot ? [alternativeSlot] : [],
        kvkkConsent: true,
        idempotencyKey: requestId,
        website: String(values.website || "")
      };

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      setBusy(true);

      try {
        const response = await fetch(API_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "Idempotency-Key": requestId
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        const responseBody = await response.json();
        const confirmedRequestId = responseBody && responseBody.requestId;
        if (typeof confirmedRequestId !== "string" || !UUID_PATTERN.test(confirmedRequestId)) {
          throw new Error("The API returned an invalid request ID");
        }

        form.hidden = true;
        successId.textContent = confirmedRequestId;
        successState.hidden = false;
        successState.focus();

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "kosk_visit_request_submitted",
          request_id: confirmedRequestId
        });
      } catch (error) {
        submitError.textContent = error && error.name === "AbortError" ? copy.timeout : copy.submit;
        submitError.hidden = false;
        submitError.focus();
        setBusy(false);
      } finally {
        window.clearTimeout(timeoutId);
      }
    });

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      const heroVideo = root.querySelector(".chp-kosk__hero-media");
      if (heroVideo) {
        heroVideo.pause();
        heroVideo.removeAttribute("autoplay");
      }
    }
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-kosk-visit-root]").forEach(init);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
